import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  maxWidth?: number;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  maxWidth = 260,
}) => {
  const { darkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; placement: 'top' | 'bottom' }>({ top: 0, left: 0, placement: 'top' });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: Event) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        tooltipRef.current && !tooltipRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  // Position the tooltip relative to viewport, accounting for edge clipping
  useEffect(() => {
    if (!open || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = Math.min(maxWidth, window.innerWidth - 16);
    const tooltipHeightEstimate = 120; // approximate

    // Decide placement: above if there's room above, otherwise below
    const placement: 'top' | 'bottom' = triggerRect.top > tooltipHeightEstimate + 16 ? 'top' : 'bottom';

    // Center horizontally on the trigger
    let left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
    // Clamp to viewport edges with 8px padding
    left = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8));

    const top = placement === 'top'
      ? triggerRect.top - 8 // tooltip's bottom near trigger top
      : triggerRect.bottom + 8;

    setPosition({ top, left, placement });
  }, [open, maxWidth]);

  return (
    <>
      <span
        ref={triggerRef}
        className="relative inline-flex"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(prev => !prev);
        }}
      >
        {children}
      </span>
      {open && (
        <div
          ref={tooltipRef}
          role="tooltip"
          className={`fixed z-[9999] px-3 py-2 rounded-lg text-xs leading-relaxed shadow-lg pointer-events-auto normal-case tracking-normal ${
            darkMode
              ? 'bg-slate-900 text-slate-200 border border-white/10'
              : 'bg-white text-slate-700 border border-slate-200'
          }`}
          style={{
            top: position.placement === 'top' ? undefined : position.top,
            bottom: position.placement === 'top' ? window.innerHeight - position.top : undefined,
            left: position.left,
            width: 'max-content',
            maxWidth: Math.min(maxWidth, window.innerWidth - 16),
          }}
        >
          {content}
        </div>
      )}
    </>
  );
};
